if (!customElements.get("text-slider")) {
  customElements.define(
    "text-slider",
    class TextSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.slideshowSelector = this.dataset.slideshowSelector;
        this.carousel = this.slideshowSelector
          ? this.querySelector(this.slideshowSelector)
          : this.querySelector(".media-icon-items-grid");
        this.initialize();
      }

      disconnectedCallback() {
        if (this.autoHeight) {
          window.removeEventListener("resize", this.handleResize);
        }
        if (this.fitToContent) {
          window.removeEventListener("resize", this.handleWidthResize);
        }
      }

      initialize() {
        const autoplayEnabled = this.getAttribute("data-autoplay") === "true";
        const autoPlaySpeed = parseInt(this.getAttribute("data-speed"), 10) * 1000;
        this.animationType = this.getAttribute("data-animation-type") ?? "slide";
        this.autoPlayEnabled = autoplayEnabled && autoPlaySpeed > 0;
        this.autoHeight = this.getAttribute("data-auto-height") === "true";
        this.fitToContent = this.getAttribute("data-fit-to-content") === "true";

        const cellSelector = this.dataset.cellSelector ?? ".media-icon-item";

        if (this.dataset.setDisplay === "true") {
          this.carousel.style.display = "block";
        }

        this.flickity = new Flickity(this.carousel, {
          autoPlay: autoplayEnabled ? autoPlaySpeed : false,
          cellAlign: "center",
          groupCells: false,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          cellSelector: cellSelector,
          initialIndex: 0,
          pageDots: false,
          wrapAround: true,
          accessibility: false,
          watchCSS: true,
        });

        if (this.autoHeight) {
          window.addEventListener("resize", this.handleResize);
        }

        if (this.fitToContent) {
          requestAnimationFrame(() => {
            this.calculateAndSetWidth();
          });
          window.addEventListener("resize", this.handleWidthResize);
        }
      }

      handleResize = debounce(() => {
        this.recalculateHeight();
      }, 200);

      handleWidthResize = debounce(() => {
        this.calculateAndSetWidth();
        if (this.flickity) {
          this.flickity.resize();
        }
      }, 200);

      /**
       * Checks if we're on desktop viewport (990px+)
       * @returns {boolean}
       */
      isDesktop() {
        return window.matchMedia("(min-width: 990px)").matches;
      }

      /**
       * Measures each slide's natural content width and sets the slider width
       * to match the widest slide (capped at max-width from CSS). Desktop only.
       */
      calculateAndSetWidth() {
        const sliderElement = this.closest(".announcement-bar__slider");

        if (!this.isDesktop()) {
          if (sliderElement) {
            sliderElement.style.removeProperty("--slider-content-width");
          }
          return;
        }

        const slides = this.carousel.querySelectorAll(
          this.dataset.cellSelector ?? ".media-icon-item"
        );

        if (!slides.length) {
          return;
        }

        let maxWidth = 0;

        slides.forEach((slide) => {
          const content = slide.querySelector(".announcement-bar__slide-content");
          if (content) {
            const icon = content.querySelector(".announcement-bar__icon");
            let contentWidth = content.offsetWidth;

            /**
             * If icon exists but offsetWidth is 0, the icon may not have rendered yet.
             * Add buffer for icon width (1.6rem = 16px) plus gap (0.6rem = 6px).
             */
            if (icon && icon.offsetWidth === 0) {
              contentWidth += 22;
            }

            maxWidth = Math.max(maxWidth, contentWidth);
          }
        });

        if (maxWidth > 0 && sliderElement) {
          sliderElement.style.setProperty(
            "--slider-content-width",
            `${maxWidth}px`
          );
        }
      }

      recalculateHeight() {
        if (!this.flickity) {
          return;
        }

        const slides = this.flickity.getCellElements();
        const maxHeight = slides.reduce((tallest, slide) => {
          return Math.max(tallest, slide.offsetHeight);
        }, 0);

        const viewport = this.querySelector(".flickity-viewport");
        if (viewport) {
          viewport.style.height = `${maxHeight}px`;
        }
      }
    },
  );
}

if (!customElements.get("text-slider-navigation")) {
  customElements.define(
    "text-slider-navigation",
    class TextSliderNavigation extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.init();
      }

      init(retry = 1, maxRetry = 3) {
        if (retry === maxRetry) {
          return;
        }

        const id = this.getAttribute("for");
        this.carouselComponent = document.querySelector(`#${id}`);
        this.wrap = this.dataset.wrap === "true";

        if (!this.carouselComponent || !this.carouselComponent.flickity) {
          setTimeout(() => {
            this.init(retry + 1);
          }, 350);
          return;
        }

        this.flickity = this.carouselComponent.flickity;
        this.buttonPrev = this.querySelector(".js-prev");
        this.buttonNext = this.querySelector(".js-next");

        this.buttonPrev.addEventListener("click", (event) => {
          event.preventDefault();
          this.flickity.previous();
        });

        this.buttonNext.addEventListener("click", (event) => {
          event.preventDefault();
          this.flickity.next();
        });

        this.flickity.on("select", () => {
          if (this.wrap) {
            return;
          }

          this.enableButton(this.buttonPrev);
          this.enableButton(this.buttonNext);

          if (!this.flickity.slides[this.flickity.selectedIndex - 1]) {
            this.disableButton(this.buttonPrev);
          } else if (!this.flickity.slides[this.flickity.selectedIndex + 1]) {
            this.disableButton(this.buttonNext);
          }
        });
      }

      enableButton(button) {
        button.classList.remove("pill-nav-item-disabled");
        button.classList.remove("focus-none");
        button.setAttribute("aria-disabled", "false");
      }

      disableButton(button) {
        button.classList.add("pill-nav-item-disabled");
        button.classList.add("focus-none");
        button.setAttribute("aria-disabled", "true");
      }
    },
  );
}
